import { createSignal, Show } from "solid-js";
import { Button } from "./button";

// A crash is often a stale client: a browser/PWA still running an old precached
// bundle against a redeployed API whose response shape changed. `reset()` just
// re-renders the same already-loaded modules, so it re-crashes on the spot. To
// actually recover we have to drop the cached assets and pull the latest build:
// unregister the service worker (it serves the precached old shell + chunks) and
// wipe Cache Storage, then reload so the next document fetch hits the network.
async function reloadLatest() {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // Best effort - reload regardless so the user is never stranded on the
    // crash screen if cache teardown is unavailable or fails.
  }
  window.location.reload();
}

export function ErrorFallback(props: { error: unknown; reset: () => void }) {
  const err = () => props.error instanceof Error ? props.error : new Error(String(props.error));
  const [reloading, setReloading] = createSignal(false);

  const handleReload = () => {
    if (reloading()) return;
    setReloading(true);
    void reloadLatest();
  };

  return (
    <div class="min-h-[80vh] flex items-center justify-center p-6">
      <div class="max-w-xl w-full space-y-5">
        <div class="space-y-1">
          <p class="text-label-xs text-brand uppercase tracking-widest font-semibold">System Error</p>
          <h1 class="font-display text-3xl font-black tracking-display text-text-base">
            Something crashed.
          </h1>
          <p class="text-sm text-text-muted">{err().message || "An unexpected error occurred."}</p>
        </div>

        <Show when={err().stack}>
          <details class="group">
            <summary class="text-label-sm text-text-faint cursor-pointer select-none hover:text-text-muted transition-colors">
              Stack trace
            </summary>
            <pre class="mt-2 text-label-xs text-text-faint bg-surface-2 border border-border rounded-xl p-4 overflow-auto max-h-64 leading-relaxed whitespace-pre-wrap">
              {err().stack}
            </pre>
          </details>
        </Show>

        <div class="space-y-2">
          <div class="flex flex-wrap gap-3">
            <Button onClick={handleReload} disabled={reloading()}>
              {reloading() ? "Reloading…" : "Reload latest version"}
            </Button>
            <Button variant="outline" onClick={props.reset}>Try again</Button>
            <Button variant="ghost" onClick={() => window.location.assign("/")}>Go home</Button>
          </div>
          <p class="text-label-xs text-text-faint">
            If the crash repeats, reload - it clears cached files and pulls the latest build.
          </p>
        </div>
      </div>
    </div>
  );
}
